// src/accounts/pages/AccountsPage.jsx
//
// Accounts admin list page. Route: /admin/accounts.
// Reads from the shared WorkspaceContext (mounted at the app root) so this
// always reflects the same list the topbar's Account switcher shows.
//
// # Projects per account isn't on AccountResponse, so it's derived here
// from a page-local, unscoped engagements fetch (conductorApi.listEngagements()
// with no account_id returns the whole org) rather than added to global state.
//
// Leads with a KPI summary row (StatTile) rather than dropping straight
// into a bare table, and gives each row a colored avatar chip, a proper
// "Selected" badge, and a quick "View Projects" action — a plain table
// with plain text read as too basic next to GoLive/Connections' cards.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Building2, Plus, FolderGit2, CheckCircle2, Pencil, Power, Zap, ShieldCheck } from "lucide-react";
import { PageHeader, Button, EmptyState, DataTable, StatusBadge, ConfirmModal, SkeletonCardGrid, SkeletonTable } from "../../shared/ui";
import { useAuth } from "../../context/AuthContext";
import { useWorkspace } from "../../context/WorkspaceContext";
import { conductorApi } from "../../api/conductorApi";
import RegisterAccountModal from "../components/RegisterAccountModal";
import AccountAvatar from "../components/AccountAvatar";
import StatTile from "../components/StatTile";

export default function AccountsPage() {
  const { accounts, loading, error, currentAccountId, createAccount, updateAccount, deactivateAccount, activateAccount, selectAccount } = useWorkspace();
  // account:manage is also grantable account-scoped (account_admin), which
  // satisfies rename/deactivate/role-management for that one account but
  // can never satisfy POST /accounts (no account_id yet to scope against —
  // see account_controller.py's create_account) -- hasGlobalPermission is
  // the primitive built for exactly this "must be a true org-wide grant"
  // check, so an account_admin without also being org_admin/super_admin
  // doesn't get a Register Account button that would just 403.
  const { hasGlobalPermission, hasPermission } = useAuth();
  const canCreateAccount = hasGlobalPermission("account:manage");
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [projectCounts, setProjectCounts] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    conductorApi
      .listEngagements()
      .then((result) => {
        if (cancelled) return;
        const counts = {};
        for (const e of result.data || []) {
          if (e.account_id != null) counts[e.account_id] = (counts[e.account_id] || 0) + 1;
        }
        setProjectCounts(counts);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [accounts]);

  const totalProjects = Object.values(projectCounts).reduce((sum, n) => sum + n, 0);
  const activeCount = accounts.filter((a) => a.is_active).length;

  async function handleRegister(payload) {
    if (editAccount) {
      const account = await updateAccount(editAccount.account_id, { name: payload.name });
      setEditAccount(null);
      return account;
    }
    return createAccount({ name: payload.name });
  }

  async function handleDeactivate(account) {
    setBusyId(account.account_id);
    try {
      await deactivateAccount(account.account_id);
      setConfirmDeactivate(null);
    } finally {
      setBusyId(null);
    }
  }

  async function handleActivate(account) {
    setBusyId(account.account_id);
    try {
      await activateAccount(account.account_id);
    } finally {
      setBusyId(null);
    }
  }

  function viewProjects(accountId) {
    selectAccount(accountId);
    navigate("/admin/projects");
  }

  // Row click (on the name) → selects that Account for the rest of the app.
  // There's no separate read-only detail view yet — per §4.3 "can be a
  // simple read-only panel for v1", selecting it is the v1 stand-in: it's
  // immediately reflected in the topbar switcher and the Projects page.
  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => {
          const account = row.original;
          const isSelected = account.account_id === currentAccountId;
          return (
            <button
              type="button"
              onClick={() => selectAccount(account.account_id)}
              className="flex items-center gap-3 group text-left"
            >
              <AccountAvatar name={account.name} size={32} />
              <span className="flex items-center gap-2">
                <span className={`font-medium group-hover:underline ${isSelected ? "text-brand-700" : "text-ink"}`}>
                  {account.name}
                </span>
                {isSelected && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 border border-brand-100">
                    Selected
                  </span>
                )}
              </span>
            </button>
          );
        },
      },
      {
        accessorKey: "is_active",
        header: "Status",
        width: 120,
        cell: ({ getValue }) => (
          <StatusBadge map="accountStatus" status={getValue() ? "active" : "inactive"} size="sm" />
        ),
      },
      {
        id: "projectCount",
        header: "# Projects",
        align: "center",
        width: 100,
        cell: ({ row }) => (
          <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full bg-gray-100 text-slate-700 text-xs font-semibold">
            {projectCounts[row.original.account_id] || 0}
          </span>
        ),
      },
      {
        id: "roleGrants",
        header: "Role grants",
        align: "center",
        width: 100,
        cell: ({ row }) => (
          <span className="inline-flex items-center justify-center min-w-[1.75rem] px-2 py-0.5 rounded-full bg-gray-100 text-slate-700 text-xs font-semibold">
            {row.original.role_bindings?.length || 0}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        align: "right",
        width: 260,
        sortAllowed: false,
        cell: ({ row }) => {
          const account = row.original;
          const isBusy = busyId === account.account_id;
          // account:manage is resolved per-account (account_admin holds it
          // only on the specific account(s) they're granted on, not every
          // row this table happens to list) -- hasPermission's context form
          // checks that account's own bucket instead of the global grant
          // hasGlobalPermission would use, so Roles/Rename/Deactivate only
          // show on rows the caller can actually act on.
          const canManage = hasPermission("account:manage", { accountId: account.account_id });
          return (
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => viewProjects(account.account_id)}
                className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-800 hover:underline"
              >
                <FolderGit2 size={13} /> Projects
              </button>
              {canManage && (
                <Link
                  to={`/admin/accounts/${account.account_id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-800 hover:underline"
                >
                  <ShieldCheck size={13} /> Roles
                </Link>
              )}
              {canManage && (
                <button
                  type="button"
                  onClick={() => setEditAccount(account)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-muted hover:text-ink"
                >
                  <Pencil size={13} /> Rename
                </button>
              )}
              {canManage && account.is_active && (
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => setConfirmDeactivate(account)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-status-critical hover:text-red-700"
                >
                  <Power size={13} /> Deactivate
                </button>
              )}
              {canManage && !account.is_active && (
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => handleActivate(account)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-800"
                >
                  <Zap size={13} /> Reactivate
                </button>
              )}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentAccountId, selectAccount, projectCounts, busyId, hasPermission]
  );

  return (
    <div>
      <PageHeader
        title="Accounts"
        subtitle="Every Account (client) Jade Orion is registered for — not to be confused with your Organization, the tenant every record here belongs to."
        actions={
          canCreateAccount && (
            <Button variant="primary" onClick={() => setRegisterOpen(true)}>
              <Plus size={15} /> Register Account
            </Button>
          )
        }
      />

      {!error && (loading || accounts.length > 0) && (
        loading ? (
          <SkeletonCardGrid cards={3} className="mb-5" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
            <StatTile icon={Building2} label="Total Accounts" value={accounts.length} />
            <StatTile icon={FolderGit2} label="Total Projects" value={totalProjects} />
            <StatTile icon={CheckCircle2} label="Active Accounts" value={activeCount} />
          </div>
        )
      )}

      {loading ? (
        <SkeletonTable rows={5} columns={5} className="rounded-card border border-line bg-white" />
      ) : error ? (
        <EmptyState
          icon={Building2}
          title="Couldn't load Accounts"
          description={error}
        />
      ) : accounts.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No Accounts yet"
          description="Register your first Account to start onboarding Projects under it."
          action={
            canCreateAccount && (
              <Button variant="secondary" onClick={() => setRegisterOpen(true)}>
                <Plus size={15} /> Register Account
              </Button>
            )
          }
        />
      ) : (
        <DataTable table={{ columns, rows: accounts }} canSearch />
      )}

      <RegisterAccountModal
        open={registerOpen || Boolean(editAccount)}
        editAccount={editAccount}
        onClose={() => { setRegisterOpen(false); setEditAccount(null); }}
        onRegister={handleRegister}
      />

      <ConfirmModal
        open={Boolean(confirmDeactivate)}
        entityLabel="account"
        name={confirmDeactivate?.name}
        onClose={() => setConfirmDeactivate(null)}
        onConfirm={() => handleDeactivate(confirmDeactivate)}
      >
        Everyone under this Account loses access until it's reactivated. Its Projects and role
        grants aren't deleted — reactivating restores them.
      </ConfirmModal>
    </div>
  );
}
